import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { accordionGroupOrder, accordionInterface } from 'src/app/util/interfaces/accordion';
import { statusInterface } from 'src/app/util/interfaces/statusInterface';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { CmdbInterface } from 'src/app/util/interfaces/ci';
import { searchRequestsInterface } from 'src/app/util/interfaces/search';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';

@Injectable({
	providedIn: 'root',
})
export class HomePageService {
	private getAvailableAccordionUrl: string = environment.baseApiUrl + 'requestHome/accordion/basic';
	private getAvailableGroupAccordionUrl = environment.baseApiUrl + 'requestHome/accordion/group';
	private getGroupExecutionAccordionUrl = environment.baseApiUrl + 'requestHome/taskGroup/execution';
	private getAvailableChecklistAccordionUrl = environment.baseApiUrl + 'requestHome/accordion/checklist';
	private getAvailableCustomAccordionUrl = environment.baseApiUrl + 'requestHome/accordion/custom';
	private getAvailableTaskAccordionUrl: string = environment.baseApiUrl + 'requestHome/accordion/task';
	private getAvailableOutOfOfficeAccordionUrl: string = environment.baseApiUrl + 'requestHome/accordion/outOfOffice';

	private getAccordionUrl: string = environment.baseApiUrl + 'requestHome/accordion/{typeAccordion}/{idAccordion}';
	private getAccordionOrderUrl: string = environment.baseApiUrl + 'requestHome/accordion/order';
	private updateAccordionOrderUrl: string = environment.baseApiUrl + 'requestHome/accordion/order';

	private getAllStatusUrl = environment.baseApiUrl + 'request/status';
	private getAllCmdbUrl = environment.baseApiUrl + 'request/cmdb';
	private getAvailableViewItems = environment.baseApiUrl + 'request/availableViewItems';

	private getRequestFieldOptionsURL = environment.baseApiUrl + 'tool/requests/fields/options';
	private getSelectOptionsURL = environment.baseApiUrl + 'tool/requests/fields/options/select';
	private getSearchRequestsOrCustomFieldsURL = environment.baseApiUrl + 'tool/requests/search';

	private getTableColumnsUrl = environment.baseApiUrl + 'mspTable/column';

	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService,
		private NotificationService: NotificationService
	) {}

	getAvailableAccordion(accordionType: string): Observable<accordionInterface[]> {
		if (
			this.AuthLoginService.getSessionStatus() === '0' &&
			accordionType !== 'basic' &&
			this.AuthLoginService.getSessionStatus() === '0' &&
			accordionType !== 'task' &&
			this.AuthLoginService.getSessionStatus() === '0' &&
			accordionType !== 'custom'
		)
			return of([]);

		let requestUrl = '';
		if (accordionType === 'basic') requestUrl = this.getAvailableAccordionUrl;
		if (accordionType === 'group') requestUrl = this.getAvailableGroupAccordionUrl;
		if (accordionType === 'groupExecution') requestUrl = this.getGroupExecutionAccordionUrl;
		if (accordionType === 'custom') requestUrl = this.getAvailableCustomAccordionUrl;
		if (accordionType === 'task') requestUrl = this.getAvailableTaskAccordionUrl;
		if (accordionType === 'outOfOffice') requestUrl = this.getAvailableOutOfOfficeAccordionUrl;
		if (accordionType === 'checklist') requestUrl = this.getAvailableChecklistAccordionUrl;

		return this.http
			.get<any>(requestUrl, { headers: this.headers })
			.pipe(
				catchError((error) => {
					return of([]);
				})
			)
			.pipe(
				map((accordionRes: any) => {
					const accordionArray: accordionInterface[] = [];

					const responseAccordions =
						accordionType === 'groupExecution'
							? Object.keys(accordionRes?.[0] || {}).map((name) => ({
									id: accordionRes[0][name].id,
									name: name,
									accordionLength: accordionRes[0][name].count,
								}))
							: accordionRes;

					for (let i = 0; i < responseAccordions.length; i++) {
						const accordion = responseAccordions[i];
						const dataType = accordion.dataType || accordion.type || 'request';
						const newAccordion: accordionInterface = {
							id: accordion.id ? accordion.id : accordion.name.replaceAll(' ', '_'),
							name: accordion.name,
							type: (accordionType === 'groupExecution' ? 'group' : accordionType) as accordionInterface['type'],
							dataType: dataType,
							border: accordion.border,
							alert: accordion.alert,
							hidden: false,
							accordionLength: accordion.accordionLength,
						};
						if (accordionType === 'groupExecution') {
							newAccordion.dataType = 'task';
							newAccordion.taskGroupExecution = true;
						}

						newAccordion[dataType] = [];

						accordionArray.push(newAccordion);
					}

					return accordionArray;
				})
			);
	}

	getAccordionOrder(): Observable<accordionGroupOrder[]> {
		return this.http.get<accordionGroupOrder[]>(this.getAccordionOrderUrl, { headers: this.headers });
	}

	updateAccordionOrder(body: accordionGroupOrder[]): Observable<any> {
		return this.http.post(this.updateAccordionOrderUrl, body, { headers: this.headers });
	}

	getAccordionData(
		typeAccordion: string,
		idAccordion: string,
		sortBy: string,
		sortDown: boolean,
		page?: number,
		itemsPerPage?: number
	): Observable<any> {
		const getAccordionUrl = this.getAccordionUrl.replace('{typeAccordion}', typeAccordion).replace('{idAccordion}', idAccordion);
		const body = {
			sortBy: sortBy,
			sortDown: sortDown,
		};
		if (page) body['page'] = page;
		if (itemsPerPage) body['itemsPerPage'] = itemsPerPage;

		return this.http.get<accordionInterface>(getAccordionUrl, { headers: this.headers, params: body });
	}

	getAccordionTaskData(task: string, sortBy: string, sortDown: boolean, page?: number, itemsPerPage?: number): Observable<any[]> {
		const getAccordionTaskUrl = environment.baseApiUrl + `requestHome/task/${task}`;
		let params = new HttpParams().set('sortBy', sortBy).set('sortDown', sortDown.toString());
		if (page !== undefined) params = params.set('page', page.toString());
		if (itemsPerPage !== undefined) params = params.set('itemsPerPage', itemsPerPage.toString());

		return this.http.get<any[]>(getAccordionTaskUrl, { headers: this.headers, params: params });
	}

	getGroupExecutionData(
		idSolutionArea: string,
		sortBy: string,
		sortDown: boolean,
		page?: number,
		itemsPerPage?: number
	): Observable<any[]> {
		const url = environment.baseApiUrl + `requestHome/taskGroup/data/execution/${idSolutionArea}`;
		let params = new HttpParams().set('sortBy', sortBy).set('sortDown', sortDown.toString());
		if (page !== undefined) params = params.set('page', page.toString());
		if (itemsPerPage !== undefined) params = params.set('itemsPerPage', itemsPerPage.toString());

		return this.http.get<any[]>(url, { headers: this.headers, params: params });
	}

	getAllStatus(): Observable<statusInterface[]> {
		return this.http.get<statusInterface[]>(this.getAllStatusUrl, { headers: this.headers });
	}

	getAvailableRequestViewItems(solutionArea?: string): Observable<genericFormI[]> {
		let parameters = new HttpParams();
		if (solutionArea) {
			parameters = parameters.append('id_solution_area', solutionArea);
		}
		return this.http.get<any[]>(this.getAvailableViewItems, { headers: this.headers, params: parameters }).pipe(
			map((requestViewItemsRes) => {
				const returnGeneric = new genericFormI('multi-select');
				const requestViewItems = Object.keys(requestViewItemsRes);
				for (const field of requestViewItems) {
					let type: genericFormI['inputType'] = 'string';
					if (field.includes('date')) type = 'date';
					const genericItem = new genericFormI(type);
					genericItem.id = field;
					genericItem.values[0] = field;
					genericItem.rawValues[0] = field;
					genericItem.name = field;
					genericItem.label = requestViewItemsRes[field];
					returnGeneric.addItem(genericItem);
				}

				returnGeneric.items.sort((a, b) => {
					const labelValueA = a[0] == '_' ? a.replace('_', '') : a;
					const labelValueB = b[0] == '_' ? b.replace('_', '') : b;
					return labelValueA > labelValueB ? 1 : labelValueA < labelValueB ? -1 : 0;
				});

				returnGeneric.boxHeight = 180;
				returnGeneric.allowDragAndDrop = true;

				return [returnGeneric];
			})
		);
	}

	getAllCMDB(): Observable<CmdbInterface[]> {
		return this.http.get<any[]>(this.getAllCmdbUrl, { headers: this.headers });
	}

	getRequestFieldOptions(): Observable<searchRequestsInterface[]> {
		return this.http.get<searchRequestsInterface[]>(this.getRequestFieldOptionsURL, { headers: this.headers });
	}

	getRequestFieldOptionsSelect(filters: any): Observable<any[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		let parameters = new HttpParams();

		for (const key of Object.keys(filters)) {
			if (filters[key]) {
				parameters = parameters.append(key, filters[key]);
			}
		}

		return this.http.get<any[]>(this.getSelectOptionsURL, { headers: headers, params: parameters });
	}

	getSearchRequests(filters: any): Observable<any[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		let parameters = new HttpParams();

		for (const key of Object.keys(filters)) {
			if (filters[key]) {
				parameters = parameters.append(key, filters[key]);
			}
		}

		return this.http.get<any[]>(this.getSearchRequestsOrCustomFieldsURL, { headers: headers, params: parameters });
	}

	setTableColumns(source: 'request', headerNameArray: string[]): Observable<any> {
		const headerNameList = headerNameArray.join(',');
		return this.http.post(this.getTableColumnsUrl, { source: source, headerNameList: headerNameList }, { headers: this.headers });
	}
}
